import ButtonTsoPq from '@components/Buttons/ButtonTsoPq';
import SegmentedButtonTsoPq from '@components/Buttons/SegmentedButtonTsoPq';
import CheckboxTsoPq from '@components/Checkbox/CheckboxTsoPq';
import ChipSelectorTsoPq from '@components/ChipSelector/ChipSelectorTsoPq';
import DatepickerTsoPq from '@components/Datepicker/DatepickerTsoPq';
import TreeAutocompleteTsoPq from '@components/TreeAutocomplete/TreeAutocompleteTsoPq';
import {
  parseSubstationDevices,
  transformHarmonicsForTreeAutocomplete,
  TreeListItemTsoPqProps,
} from '@custom-types/TreeTypes';
import {
  defaultTsos,
  StyledButtonWrapper,
  StyledLineWrapper,
  StyledMeasurementDeviceTitle,
  StyledWrapper,
} from '@pages/MeasurementVisualization/Section/MeasurementVisualizationParameters';

import { useSidePanel } from '@components/Grid/GridTsoPq';
import { TsosStateProps } from '@pages/MeasurementVisualization/utils';
import { StyledSectionWrapper } from '@pages/MeasurmentExport/MeasurmentExportStyles';
import {
  PostHarmonicBackgroundLevelGetHarmonicBackgroundLevelValuesApiResponse,
  useLazyPostHarmonicBackgroundLevelGetHarmonicBackgroundLevelValuesQuery,
} from '@redux/apiSlices/harmonicBackgroundLevelApi';
import { useGetMeasurementDeviceGetAllQuery } from '@redux/apiSlices/measurementDeviceApi';
import { useGetMeasurementParameterGetAllQuery } from '@redux/apiSlices/measurementParameterApi';
import {
  ApiDatabaseDtOsSubstationsDeviceHierarchyDto,
  useLazyGetSubstationGetSubstationDeviceHierarchyQuery,
} from '@redux/apiSlices/substationApi';
import { useGetTsoGetAllQuery } from '@redux/apiSlices/tsoApi';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import {
  resetAll,
  resetQuickSelect,
  selectActiveTsos,
  selectAllEvenHarmonics,
  selectAllOddHarmonics,
  selectDeviceTechnicalNames,
  selectEnd,
  selectMeasurementType,
  selectSelectedHarmonicsIds,
  selectSelectedHarmonicsTechnicalNames,
  selectStart,
  selectTriedSubmitting,
  setActiveTsoName,
  setActiveTsos,
  setAllEvenHarmonics,
  setAllOddHarmonics,
  setDataToBeDisplayedInChart,
  setDeviceTechnicalNames,
  setEnd,
  setHarmonicBackgroundLevel,
  setIsLoading,
  setMandatoryFieldsStatus,
  setMeasurementType,
  setSelectedHarmonicsIds,
  setSelectedHarmonicsTechnicalNames,
  setStart,
  setTriedSubmitting,
  validateMandatoryFields,
} from '@redux/slices/harmonicBackgroundLevelSlice';
import { findDeviceAndSubstation } from '@utils/deviceHierarchy';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  StyledCheckboxControlsContainer,
  StyledCheckboxControlsLabel,
  StyledCheckboxControlsWrapper,
  StyledCheckboxOptionWrapper,
  StyledDisabledLabel,
  StyledLabel,
} from './harmonicBackgroundLevelStyles';

function HarmonicBackgroundLevelParameters() {
  const { handleOpen } = useSidePanel();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [tsoDeviceOptions, setTsoDeviceOptions] = useState<{
    [key: number]: TreeListItemTsoPqProps[] | undefined;
  }>({});
  const [harmonicOrderOptions, setHarmonicOrderOptions] = useState<TreeListItemTsoPqProps[] | undefined>([]);
  const { data: tsos } = useGetTsoGetAllQuery();
  const [triggerGetDeviceHierarchy] = useLazyGetSubstationGetSubstationDeviceHierarchyQuery();
  // @ts-ignore
  const { data: measurementDevices } = useGetMeasurementDeviceGetAllQuery();
  const { data: measurementParameters } = useGetMeasurementParameterGetAllQuery();
  const [submitSelectionForHarmonicBackgroundLevel] =
    useLazyPostHarmonicBackgroundLevelGetHarmonicBackgroundLevelValuesQuery();

  const measurementTypeOptions = [
    {
      segmentValue: 'LL',
      segmentLabel: t('harmonicsBackgroundLevel.phasePhase'),
    },
    {
      segmentValue: 'LE',
      segmentLabel: t('harmonicsBackgroundLevel.phaseGround'),
    },
  ];

  const deviceTechnicalNames = useAppSelector(selectDeviceTechnicalNames);
  const start = useAppSelector(selectStart);
  const end = useAppSelector(selectEnd);
  const activeTsos = useAppSelector(selectActiveTsos);
  const activeTsoName = useAppSelector(selectActiveTsos).at(-1)?.name ?? '';
  const prevActiveTsosRef = useRef<TsosStateProps[]>([]);

  const allEvenHarmonics = useAppSelector(selectAllEvenHarmonics);
  const allOddHarmonics = useAppSelector(selectAllOddHarmonics);
  const selectedHarmonicsTechnicalNames = useAppSelector(selectSelectedHarmonicsTechnicalNames);
  const selectedHarmonicsIds = useAppSelector(selectSelectedHarmonicsIds);
  const measurementType = useAppSelector(selectMeasurementType);
  const mandatoryFieldsStatus = useAppSelector((state) => state.harmonicBackgroundLevel.mandatoryFieldsStatus);
  const triedSubmiting = useAppSelector(selectTriedSubmitting);
  const reduxState = useAppSelector((state) => state.harmonicBackgroundLevel);

  type SelectedDevice = {
    deviceLabel?: string;
    measurementPointLabel?: string;
    substationLabel?: string;
    deviceId?: number;
    tsoId?: number;
    entityId?: number;
    technicalName?: string;
  };

  const getHierarchyForTso = (tsoId: number): any => rawTsoDeviceTree?.[tsoId] ?? [];
  const getDeviceIdsForTso = (tsoId: number): string[] => deviceTechnicalNames?.[tsoId] ?? [];
  const extractEntityId = (fullId: string): number => Number(fullId.split('-').at(-1));
  const getTechnicalName = (entityId: number): string | undefined =>
    measurementDevices?.externalDtos?.find((d) => d.id === entityId)?.technicalName;
  const buildSelectedDevice = (hierarchy: any, fullId: string): SelectedDevice | null => {
    const entityId = extractEntityId(fullId);
    const metadata = findDeviceAndSubstation(hierarchy, entityId);
    const technicalName = getTechnicalName(entityId);
    return metadata ? { ...metadata, technicalName } : null;
  };

  const buildSelectedDevices = (): SelectedDevice[] =>
    activeTsos.flatMap((tso) => {
      const hierarchy = getHierarchyForTso(tso.id);
      const deviceIds = getDeviceIdsForTso(tso.id);

      return deviceIds.map((id) => buildSelectedDevice(hierarchy, id)).filter((d): d is SelectedDevice => d !== null);
    });

  const buildPayloads = (devices: ReturnType<typeof buildSelectedDevices>) =>
    devices.map((device) => ({
      from: start,
      to: end,
      tsoId: device.tsoId!,
      measurementDeviceTechnicalName: device.technicalName ?? '',
      body: selectedHarmonicsIds,
    }));

  const submitAll = async () => {
    dispatch(setTriedSubmitting(true));

    const { mandatoryFieldsStatus, allValid } = validateMandatoryFields(reduxState);

    dispatch(setMandatoryFieldsStatus(mandatoryFieldsStatus));
    if (!allValid) return;

    dispatch(setIsLoading(true));
    const allSelectedDevices = buildSelectedDevices();
    const payloads = buildPayloads(allSelectedDevices);
    try {
      const responses = await Promise.allSettled(
        payloads.map((payload) => submitSelectionForHarmonicBackgroundLevel(payload).unwrap())
      );

      const successfulResponses = responses
        .map((result, index) => ({ result, index }))
        .filter(({ result }) => result.status === 'fulfilled')
        .map(({ result, index }) => ({
          data: (
            result as PromiseFulfilledResult<PostHarmonicBackgroundLevelGetHarmonicBackgroundLevelValuesApiResponse>
          ).value,
          index,
        }));

      successfulResponses.forEach(({ data, index }) => {
        const device = allSelectedDevices[index];
        const deviceKey = `${payloads[index].tsoId}-${device.entityId}`;
        dispatch(setHarmonicBackgroundLevel({ deviceKey, data }));
      });

      dispatch(
        setDataToBeDisplayedInChart(
          // @ts-ignore
          successfulResponses.map(({ data, index }) => {
            const device = allSelectedDevices[index];
            return {
              tsoId: device.tsoId!,
              measurementDeviceId: device.entityId!,
              technicalName: device.technicalName,
              deviceLabel: device.deviceLabel,
              substationLabel: device.substationLabel,
              measurementPointLabel: device.measurementPointLabel,
              start: start!,
              end: end!,
              harmonics: data.map((entry, i) => ({
                ...entry,
                orderLabel: selectedHarmonicsIds[i],
              })),
            };
          })
        )
      );
    } catch (err) {
      console.error('Error while submitting harmonic background level:', err);
    } finally {
      dispatch(setIsLoading(false));
      handleOpen(false);
    }
  };

  const handleStartChange = (date: dayjs.Dayjs) => {
    dispatch(setStart(dayjs(date).format('YYYY-MM-DD')));
  };

  const handleEndChange = (date: dayjs.Dayjs) => {
    dispatch(setEnd(dayjs(date).format('YYYY-MM-DD')));
  };

  const handleActiveTsosChange = (tsos: TsosStateProps[]) => {
    const lastActiveTsoName = tsos.at(-1)?.name ?? '';
    const selectedTsosIds = tsos.map((tso) => `${tso.id}`);
    const tsosWithSelectedDevices = Object.keys(deviceTechnicalNames);
    const updatedDeviceTechnicalNames = { ...deviceTechnicalNames };
    const unselectedTsos = tsosWithSelectedDevices.find((tsoId) => !selectedTsosIds.includes(tsoId));

    if (unselectedTsos) {
      delete updatedDeviceTechnicalNames[unselectedTsos];
    }

    dispatch(setDeviceTechnicalNames(updatedDeviceTechnicalNames));
    dispatch(setActiveTsos(tsos));
    dispatch(setActiveTsoName(lastActiveTsoName));
  };

  const [rawTsoDeviceTree, setRawTsoDeviceTree] = useState<{
    [key: number]: ApiDatabaseDtOsSubstationsDeviceHierarchyDto[] | undefined;
  }>({});

  const handleMeasurementTypeChange = (value: string[]) => {
    const selected = value[0] as 'LL' | 'LE' | undefined;
    if (!selected) return;
    dispatch(setMeasurementType(selected));
    dispatch(resetQuickSelect());
    dispatch(setSelectedHarmonicsTechnicalNames([]));
    dispatch(setSelectedHarmonicsIds([]));
  };

  const handleHarmonicChange = (selected: string[]) => {
    if (selected.length === 0) {
      dispatch(setAllEvenHarmonics(false));
      dispatch(setAllOddHarmonics(false));
    }

    const selectedOptions = harmonicOrderOptions
      ?.filter((harmonic) => selected.includes(harmonic.id))
      .map((filteredHarmonic) => filteredHarmonic.subcategoryParameters)
      .flat(2);

    dispatch(setSelectedHarmonicsTechnicalNames(selected));
    dispatch(setSelectedHarmonicsIds(selectedOptions));
  };

  const updateSelectedValues = <T,>(currentValues: T[], nextValues: T[], checked: boolean) => {
    if (checked) {
      return [...new Set([...currentValues, ...nextValues])];
    }

    return currentValues.filter((value) => !nextValues.includes(value));
  };

  const onChangeCheckbox = ({ checked, harmonics }: { checked: boolean; harmonics: 'even' | 'odd' }) => {
    if (harmonics === 'even') {
      dispatch(setAllEvenHarmonics(checked));
    } else {
      dispatch(setAllOddHarmonics(checked));
    }

    let phaseType: 'phasephase' | 'phaseground' | undefined;
    if (measurementType === 'LL') {
      phaseType = 'phasephase';
    } else if (measurementType === 'LE') {
      phaseType = 'phaseground';
    }

    const parametersForType =
      measurementParameters?.externalDtos?.filter(
        (param) =>
          param.isDisplayedForHarmonicBackgroundLevel &&
          param.categoryPath?.split('.')?.includes(harmonics) &&
          (phaseType ? param.categoryPath?.includes(phaseType) : true)
      ) ?? [];

    const subcategoryTechnicalNames = [
      ...new Set(parametersForType.map((p) => p.categoryPath?.split('.').at(-1)).filter((value): value is string => !!value)),
    ];
    const harmonicParameterIds = parametersForType
      .map((parameter) => parameter.id)
      .filter((id): id is number => typeof id === 'number');
    const updatedSubcategoriesTechnicalNames = updateSelectedValues(
      selectedHarmonicsTechnicalNames ?? [],
      subcategoryTechnicalNames,
      checked
    );
    const updatedHarmonicIds = updateSelectedValues(selectedHarmonicsIds ?? [], harmonicParameterIds, checked);

    dispatch(setSelectedHarmonicsTechnicalNames(updatedSubcategoriesTechnicalNames));
    dispatch(setSelectedHarmonicsIds(updatedHarmonicIds));
  };

  const handleDevicesChange = (id: number, selectedDevices: string[]) => {
    const updatedDeviceTechnicalNames = {
      ...deviceTechnicalNames,
      [id.toString()]: selectedDevices,
    };
    if (selectedDevices.length === 0) {
      updatedDeviceTechnicalNames[id] = [];
    }

    dispatch(setDeviceTechnicalNames(updatedDeviceTechnicalNames));
  };

  const handleReset = () => {
    dispatch(resetAll());
  };

  useEffect(() => {
    const prevTsoIds = prevActiveTsosRef.current.map((tso) => tso.id);
    const newTso = activeTsos.find((tso) => !prevTsoIds.includes(tso.id));

    if (newTso && !tsoDeviceOptions[newTso.id]) {
      const fetchDevices = async () => {
        try {
          const result = await triggerGetDeviceHierarchy(newTso.id, true);
          const rawTree = result.data?.externalDtos;

          if (rawTree) {
            setRawTsoDeviceTree((prev) => ({ ...prev, [newTso.id]: rawTree }));
            const parsedTree = parseSubstationDevices(rawTree);
            if (parsedTree) {
              setTsoDeviceOptions((prev) => ({ ...prev, [newTso.id]: parsedTree }));
            }
          }
        } catch (error) {
          console.error('Error on "trigger device hierarchy" : ', error);
        }
      };

      fetchDevices();
    }
    prevActiveTsosRef.current = activeTsos;
  }, [activeTsos, activeTsoName]);

  useEffect(() => {
    const allParameters = measurementParameters?.externalDtos;
    if (!allParameters) return;

    const phaseType = measurementType === 'LL' ? 'phasephase' : 'phaseground';

    const filteredForHarmonics = allParameters.filter((parameter) => {
      if (!parameter.isDisplayedForHarmonicBackgroundLevel) return false;
      if (!measurementType) return true;
      return parameter.categoryPath?.includes(phaseType) ?? false;
    });

    const harmonicsOptions = filteredForHarmonics && transformHarmonicsForTreeAutocomplete(filteredForHarmonics, t);
    const harmonicOrders = harmonicsOptions?.at(0);
    const sortedHarmonicOrders =
      harmonicOrders?.options?.sort((orderA, orderB) =>
        orderA.label.localeCompare(orderB.label, undefined, { numeric: true, sensitivity: 'base' })
      ) ?? [];
    setHarmonicOrderOptions(sortedHarmonicOrders);
  }, [measurementParameters, measurementType, t]);

  return (
    <StyledWrapper>
      <StyledSectionWrapper>
        <ChipSelectorTsoPq
          customWidth="30.5rem"
          label={t('harmonicsBackgroundLevel.tso')}
          // @ts-ignore
          chips={tsos?.externalDtos ?? defaultTsos}
          error={mandatoryFieldsStatus.tso === false}
          supportingText={mandatoryFieldsStatus.tso === false ? t('harmonicsBackgroundLevel.tso.isRequired') : ''}
          activeChips={activeTsos}
          handleChange={handleActiveTsosChange}
          mandatory
        />
        <StyledLineWrapper>
          <DatepickerTsoPq
            label={t('harmonicsBackgroundLevel.start')}
            type="datepicker"
            width="15rem"
            enableFutureDates={false}
            error={mandatoryFieldsStatus.start === false}
            supportingText={
              mandatoryFieldsStatus.start === false
                ? `${t('harmonicsBackgroundLevel.start')} ${t('general.error.required')}`
                : ''
            }
            date={start}
            handleChange={handleStartChange}
            mandatory
          />
          <DatepickerTsoPq
            label={t('harmonicsBackgroundLevel.end')}
            supportingText={
              mandatoryFieldsStatus.end === false
                ? `${t('harmonicsBackgroundLevel.end')} ${t('general.error.required')}`
                : ''
            }
            type="datepicker"
            width="15rem"
            enableFutureDates={false}
            error={mandatoryFieldsStatus.end === false}
            handleChange={handleEndChange}
            date={end}
            minDateTime={dayjs(start)}
            mandatory
          />
        </StyledLineWrapper>
        <SegmentedButtonTsoPq
          customWidth="31.5rem"
          centerAlignment
          options={measurementTypeOptions}
          sizeVariant="m"
          handleChange={handleMeasurementTypeChange}
          defaultValue={measurementType ?? ''}
          error={mandatoryFieldsStatus.measurementType === false}
          supportingText={
            mandatoryFieldsStatus.measurementType === false
              ? `${t('harmonicsBackgroundLevel.measurementTypeLabel.isRequired')}`
              : ''
          }
          mandatory
          label={t('harmonicsBackgroundLevel.measurementTypeLabel')}
        />
        <StyledCheckboxControlsContainer>
          <StyledCheckboxControlsLabel>{t('harmonicsBackgroundLevel.quickSelect')}</StyledCheckboxControlsLabel>
          <StyledCheckboxControlsWrapper>
            <StyledCheckboxOptionWrapper
              control={
                <CheckboxTsoPq
                  disabled={measurementType === ''}
                  checked={allEvenHarmonics}
                  onChange={(e) => onChangeCheckbox({ checked: e.target.checked, harmonics: 'even' })}
                />
              }
              label={
                measurementType === '' ? (
                  <StyledDisabledLabel>{t('harmonicsBackgroundLevel.allEvenHarmonics')}</StyledDisabledLabel>
                ) : (
                  <StyledLabel>{t('harmonicsBackgroundLevel.allEvenHarmonics')}</StyledLabel>
                )
              }
            />
            <StyledCheckboxOptionWrapper
              control={
                <CheckboxTsoPq
                  disabled={measurementType === ''}
                  checked={allOddHarmonics}
                  onChange={(e) => onChangeCheckbox({ checked: e.target.checked, harmonics: 'odd' })}
                />
              }
              label={
                measurementType === '' ? (
                  <StyledDisabledLabel>{t('harmonicsBackgroundLevel.allOddHarmonics')}</StyledDisabledLabel>
                ) : (
                  <StyledLabel>{t('harmonicsBackgroundLevel.allOddHarmonics')}</StyledLabel>
                )
              }
            />
          </StyledCheckboxControlsWrapper>
        </StyledCheckboxControlsContainer>
        <TreeAutocompleteTsoPq
          disabled={!measurementType}
          customwidth="31.5rem"
          options={harmonicOrderOptions ?? []}
          label={t('harmonicsBackgroundLevel.harmonicOrder')}
          placeholder={t('harmonicsBackgroundLevel.harmonicOrderPlaceholder')}
          handleChange={handleHarmonicChange}
          defaultValue={selectedHarmonicsTechnicalNames}
          mandatory
          error={triedSubmiting && mandatoryFieldsStatus.measurementType && mandatoryFieldsStatus.harmonics === false}
          supportingText={
            triedSubmiting && mandatoryFieldsStatus.measurementType && mandatoryFieldsStatus.harmonics === false
              ? `${t('harmonicsBackgroundLevel.measurementTypeLabel.isRequired')}`
              : ''
          }
        />
      </StyledSectionWrapper>
      {activeTsos?.map((tso) => (
        <StyledSectionWrapper key={tso.id} selectDevice>
          <StyledMeasurementDeviceTitle>{tso.name}</StyledMeasurementDeviceTitle>
          <TreeAutocompleteTsoPq
            customwidth="31.5rem"
            options={tsoDeviceOptions[tso.id] ?? []}
            label={t('measurementVisualization.parameters.measurementDevice')}
            placeholder={t('general.error.valueSelect')}
            handleChange={(values) => handleDevicesChange(tso.id, values)}
            defaultValue={deviceTechnicalNames?.[tso.id] ?? []}
            lowestLevel={3}
            multiple
            mandatory
            error={mandatoryFieldsStatus.selectedDevices[tso.id] === false}
            supportingText={
              mandatoryFieldsStatus.selectedDevices[tso.id] === false
                ? `${t('measurementVisualization.parameters.measurementDevice')} ${t('general.error.required')}`
                : ''
            }
          />
        </StyledSectionWrapper>
      ))}
      <StyledButtonWrapper>
        <ButtonTsoPq
          onClick={handleReset}
          sizeVariant="m"
          buttonType="outlinedPrimary"
          textContent={t('measurementVisualization.form.discard')}
        />
        <ButtonTsoPq
          onClick={submitAll}
          sizeVariant="m"
          buttonType="filledPrimary"
          textContent={t('measurementVisualization.form.submit')}
        />
      </StyledButtonWrapper>
    </StyledWrapper>
  );
}

export default HarmonicBackgroundLevelParameters;
